import { createReducer, on } from '@ngrx/store';
import { AuthState } from './auth.state';
import {
  loadTokenFromLocalStorage,
  setTokenToLocalStorage,
  refreshToken,
  setUserInfo,
  loginSuccess,
  loginFailure,
  logout,
  setUserTokenToLocalStorage,
  clearImpersonation,
  logoutAdmin,
  logoutImpersonatedUser
} from './auth.actions';
import { TokenStorageService } from '../../services/token-storage.service';

// We need to inject the service properly in Angular
let tokenStorageService: TokenStorageService;

export const initialState: AuthState = {
  userInfo: null,
  role: null,
  token: null,
  refreshToken: null,
  userToken: null,
  userRefreshToken: null
};

export const authReducer = createReducer(
  initialState,
  on(loadTokenFromLocalStorage, (state) => {
    // Initialize the service if not already done
    if (!tokenStorageService) {
      tokenStorageService = new TokenStorageService();
    }

    const token = tokenStorageService.getCurrentToken();
    const refreshToken = tokenStorageService.getCurrentRefreshToken();
    const userToken = tokenStorageService.getImpersonatedUserTokens()?.token || null;
    const userRefreshToken = tokenStorageService.getImpersonatedUserTokens()?.refreshToken || null;

    return { ...state, token, refreshToken, userToken, userRefreshToken };
  }),
  on(setTokenToLocalStorage, (state, { token, refreshToken }) => {
    if (!tokenStorageService) {
      tokenStorageService = new TokenStorageService();
    }

    tokenStorageService.setAdminTokens(token, refreshToken);
    return { ...state, token, refreshToken };
  }),
  on(refreshToken, (state, { token }) => {
    if (!tokenStorageService) {
      tokenStorageService = new TokenStorageService();
    }

    if (tokenStorageService.isImpersonationMode()) {
      // Update impersonated user token
      const currentTokens = tokenStorageService.getImpersonatedUserTokens();
      if (currentTokens) {
        tokenStorageService.setImpersonatedUserTokens(token, currentTokens.refreshToken);
      }
      return { ...state, userToken: token };
    } else {
      // Update admin token
      tokenStorageService.setAdminTokens(token, state.refreshToken);
      return { ...state, token };
    }
  }),
  on(setUserInfo, (state, { userInfo }) => ({
    ...state,
    userInfo,
    role: userInfo?.role || null
  })),
  on(loginSuccess, (state, { token, refreshToken, user }) => {
    if (!tokenStorageService) {
      tokenStorageService = new TokenStorageService();
    }

    tokenStorageService.setAdminTokens(token, refreshToken);
    return {
      ...state,
      token,
      refreshToken,
      userInfo: user,
      role: user?.role || null
    };
  }),
  on(loginFailure, (state) => ({
    ...state,
    token: null,
    refreshToken: null,
    userInfo: null,
    role: null
  })),
  on(logout, (state) => {
    if (!tokenStorageService) {
      tokenStorageService = new TokenStorageService();
    }

    tokenStorageService.clearAllTokens();
    return {
      ...state,
      token: null,
      refreshToken: null,
      userInfo: null,
      role: null,
      userToken: null,
      userRefreshToken: null
    };
  }),
  on(logoutAdmin, (state) => {
    if (!tokenStorageService) {
      tokenStorageService = new TokenStorageService();
    }

    // Clear all tokens when admin logs out
    tokenStorageService.clearAllTokens();
    return {
      ...state,
      token: null,
      refreshToken: null,
      userInfo: null,
      role: null,
      userToken: null,
      userRefreshToken: null
    };
  }),
  on(logoutImpersonatedUser, (state) => {
    if (!tokenStorageService) {
      tokenStorageService = new TokenStorageService();
    }

    // Only clear impersonated user tokens, keep admin tokens
    tokenStorageService.clearImpersonatedUserTokens();

    // Get admin tokens to restore admin session
    const adminTokens = tokenStorageService.getAdminTokens();

    return {
      ...state,
      userToken: null,
      userRefreshToken: null,
      // Keep admin token and userInfo as they should remain available
      token: adminTokens?.token || state.token,
      refreshToken: adminTokens?.refreshToken || state.refreshToken
    };
  }),
  on(setUserTokenToLocalStorage, (state, { userToken, userRefreshToken }) => {
    if (!tokenStorageService) {
      tokenStorageService = new TokenStorageService();
    }

    tokenStorageService.setImpersonatedUserTokens(userToken, userRefreshToken);
    return { ...state, userToken, userRefreshToken };
  }),
  on(clearImpersonation, (state) => {
    if (!tokenStorageService) {
      tokenStorageService = new TokenStorageService();
    }

    tokenStorageService.clearImpersonatedUserTokens();
    return { ...state, userToken: null, userRefreshToken: null };
  })
);
